import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {Field} from '../../../../interfaces';
import {Upload} from '../../../../interfaces/entities/upload';
import {ConfirmDialogComponent, DialogType} from '../../../dialogs';
import {UploadApiService} from '../../../../services/api/upload-api.service';
import {NotificationService} from '../../../../services/notification.service';
import { MatDialog } from '@angular/material/dialog';
import {from, Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, finalize, mergeMap, retry, takeUntil} from 'rxjs/operators';
import {NgxDropzoneChangeEvent} from 'ngx-dropzone';
import {PhotoUtilService} from "../../../../services/util/photo-util.service";
import {PhotoUploadTrackerService} from "../../../../services/util/photo-upload-tracker.service";
import {CdkDragDrop, moveItemInArray, transferArrayItem} from "@angular/cdk/drag-drop";
import {isMobile} from "@app/util/browser.helper";
import {FieldTypeEnum} from "../../../../enums/FieldTypeEnum";

@Component({
    selector: 'app-form-input-photos',
    templateUrl: './form-input-photos.component.html',
    styleUrls: ['./form-input-photos.component.scss'],
    standalone: false
})
export class FormInputPhotosComponent implements OnInit, OnDestroy, OnChanges {
  @Input() control: FormGroup;

  @Input() field: Field<Upload[]>;

  @Input() showGrid = true;

  public images: Image[] = [];
  public rejectedFileMessages: string[] = [];
  public fileArray = [];
  isDisabled = false;
  public isMobile = isMobile();
  protected readonly FieldTypeEnum = FieldTypeEnum;
  protected readonly advertiseOnTitle = $localize`Fotografija se uporablja za oglaševanje. Kliknite, da jo izključite.`;
  protected readonly advertiseOffTitle = $localize`Fotografija je izključena iz oglaševanja. Kliknite, da jo vključite.`;

  private statusSubscription: Subscription;

  // BRIEFD-7138: firing all uploads at once exhausts memory on mobile browsers
  // (concurrent FileReader/canvas decodes + XHRs on full-resolution photos),
  // which silently kills the tab mid-upload. Cap how many run at a time.
  private static readonly MAX_CONCURRENT_UPLOADS = 3;
  private destroy$ = new Subject<void>();

  constructor(private uploadApiService: UploadApiService,
              private notificationService: NotificationService,
              private matDialog: MatDialog,
              private photoUtil: PhotoUtilService,
              private photoUploads: PhotoUploadTrackerService,
              private ref: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.statusSubscription = this.control.statusChanges.subscribe((status) => this.isDisabled = status === 'DISABLED');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.control.currentValue) {
      if (changes.control.currentValue.value) {
        this.images = changes.control.currentValue.value.map((currentUpload) => {
          return {upload: currentUpload};
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.statusSubscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  keys(item: {}) {
    return Object.keys(item);
  }

  // BRIEFD-7138: each entry always settles — resize/upload errors are caught
  // here rather than left unhandled — so mergeMap's concurrency limit below
  // never stalls waiting on a stuck file.
  private uploadOne(picked: Promise<File>, temporaryFile: { upload?: Upload }): Observable<void> {
    return from(picked).pipe(
      mergeMap((fileToBeUploaded) => this.photoUtil.resizeImage(fileToBeUploaded)),
      mergeMap((resizedImage) => this.uploadApiService.uploadImageFile(resizedImage)),
      // BRIEFD-7644: a mobile resize or a dropped connection is usually transient; one more try
      // beats silently removing the photo.
      retry({count: 1, delay: 1000}),
      mergeMap((result) => {
        setTimeout(() => {
          temporaryFile.upload = result.data;
          this.updateControlValue();
          this.ref.detectChanges();
        }, 0);
        return of(void 0);
      }),
      catchError((error) => {
        setTimeout(() => {
          this.notificationService.showErrorAlert(undefined, error);
          const index = this.images.indexOf(temporaryFile);
          if (index !== -1) {
            this.images.splice(index, 1);
          }
          this.ref.detectChanges();
        }, 0);
        return of(void 0);
      })
    );
  }

  removeFiles() {
    const dialogRef = ConfirmDialogComponent.open(this.matDialog, DialogType.DELETE_PHOTO, true);

    dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          for (const item of this.fileArray) {
            this.images.splice(this.images.indexOf(item), 1);
          }
          this.fileArray = [];
          this.updateControlValue();
          this.ref.markForCheck();
        }
      }
    );
  }

  isBackgroundRemovalField(): boolean {
    return this.field?.config?.typeId === FieldTypeEnum.BRIEFD_VEHICLE_EXTERNAL_IMAGE_BACKGROUND_REMOVAL;
  }

  // Only vehicle photographs are published to portals, so the advertising
  // toggle is scoped to that field (BRIEFD-6768).
  isAdvertisingSelectable(): boolean {
    return this.field?.config?.typeId === FieldTypeEnum.BRIEFD_VEHICLE_PHOTOGRAPHS;
  }

  isAdvertised(item: Image): boolean {
    return item.upload?.advertise !== false;
  }

  isCarPeakPhoto(item: Image): boolean {
    return item.upload?.files?.[0]?.isProcessed === true;
  }

  toggleAdvertise(item: Image) {
    if (!item.upload) {
      return;
    }
    item.upload.advertise = !this.isAdvertised(item);
    this.updateControlValue();
    this.ref.markForCheck();
  }

  hasSelectedFiles(): boolean {
    return this.fileArray.length > 0;
  }

  onItemSelectChange(item: any) {
    if (this.checkIfItemSelected(item) === -1) {
      this.fileArray.push(item);
    } else {
      const index = this.fileArray.indexOf(item);
      this.fileArray.splice(index, 1);
    }
  }

  checkIfItemSelected(item) {
    return this.fileArray.indexOf(item);
  }

  removeFile(item: any) {
    const dialogRef = ConfirmDialogComponent.open(this.matDialog, DialogType.DELETE_PHOTO, true);

    dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.images.splice(this.images.indexOf(item), 1);
          const fileArrayIndex = this.fileArray.indexOf(item);
          if (fileArrayIndex !== -1) {
            this.fileArray.splice(fileArrayIndex, 1);
          }
          this.updateControlValue();
          this.ref.markForCheck();
        }
      }
    );
  }

  private updateControlValue() {
    this.control.setValue(this.images.filter((item) => item.upload).map((item) => item.upload));
  }

  moveBack(item: Image) {
    const index = this.images.indexOf(item);
    this.move(index, index + 1);
  }

  moveForward(item: Image) {
    const index = this.images.indexOf(item);
    this.move(index, index - 1);
  }

  private move(from, to) {
    this.images.splice(to, 0, this.images.splice(from, 1)[0]);
    this.updateControlValue();
    this.ref.markForCheck();
  }

  get maxImages(): number | null {
    return this.field?.config?.max ?? null;
  }

  get isMaxReached(): boolean {
    return this.maxImages !== null && this.images.length >= this.maxImages;
  }

  onSelect($event: NgxDropzoneChangeEvent) {
    this.notifyRejectedFiles($event.rejectedFiles);

    if ($event.addedFiles) {
      // The OS dialog decides FileList order, and it differs per OS and per
      // selection gesture. Dealers number their photos, so sort by name
      // (1, 2, … 10, not 1, 10, 2) (BRIEFD-7429, BRIEFD-7545).
      let filesToAdd = [...$event.addedFiles].sort((a, b) => a.name.localeCompare(b.name, undefined, {numeric: true}));

      if (this.maxImages !== null) {
        const remaining = this.maxImages - this.images.length;
        if (remaining <= 0) {
          this.notificationService.showInfoAlert($localize`Največje dovoljeno število fotografij je ${this.maxImages}.`);
          return;
        }
        if (filesToAdd.length > remaining) {
          filesToAdd = filesToAdd.slice(0, remaining);
          this.notificationService.showInfoAlert($localize`Največje dovoljeno število fotografij je ${this.maxImages}. Dodanih je bilo samo ${remaining} fotografij.`);
        }
      }

      const entries = filesToAdd.map((addedFile) => {
        const tempFile = {upload: null};
        this.images = [...this.images, tempFile];
        return {file: FormInputPhotosComponent.copyToMemory(addedFile), tempFile};
      });

      this.photoUploads.start();
      from(entries).pipe(
        mergeMap((entry) => this.uploadOne(entry.file, entry.tempFile), FormInputPhotosComponent.MAX_CONCURRENT_UPLOADS),
        takeUntil(this.destroy$),
        // After uploadOne's setTimeout has written the last photo into the control value.
        finalize(() => setTimeout(() => this.photoUploads.finish(), 0))
      ).subscribe();
    }
  }

  /** BRIEFD-7705: only MAX_CONCURRENT_UPLOADS files are read at once, so the rest were read seconds
   *  after the pick. Windows can no longer read some picked files by then (OneDrive placeholders,
   *  the Photos app, a phone over USB), which failed every photo after the first. Read them all now. */
  // ponytail: holds the whole selection in memory; stream per file if large batches run out of memory.
  private static copyToMemory(file: File): Promise<File> {
    return file.arrayBuffer().then((buffer) => new File([buffer], file.name, {type: file.type, lastModified: file.lastModified}));
  }

  private notifyRejectedFiles(rejectedFiles: NgxDropzoneChangeEvent['rejectedFiles']): void {
    if (!rejectedFiles?.length) {
      this.rejectedFileMessages = [];
      return;
    }

    const maxMb = Math.round((this.field?.config?.maxFileSize ?? 5120) / 1024);

    this.rejectedFileMessages = rejectedFiles.map((file) => {
      switch (file.reason) {
        case 'size':
          return $localize`:@@photos.rejected-size:Slika "${file.name}" je prevelika (največ ${maxMb} MB).`;
        case 'type':
          return $localize`:@@photos.rejected-type:Slika "${file.name}" ni v podprti obliki (JPEG, PNG, GIF).`;
        default:
          return $localize`:@@photos.rejected-generic:Slike "${file.name}" ni bilo mogoče dodati.`;
      }
    });

    this.ref.detectChanges();
  }

  onDrop(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(this.images, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
    this.updateControlValue();
  }
}

interface Image {
  upload?: Upload;
}
